import React from "react";
import { LangLink as Link } from "@/components/LangLink";
import { Calendar, Globe2, Repeat2, BedDouble, Receipt, TrendingUp, Puzzle, BarChart3, Sparkles } from "lucide-react";
import SEO from "@/components/SEO";
import PageHeader from "@/components/PageHeader";
import FinalCTA from "@/components/FinalCTA";
import { useT } from "@/hooks/useT";

export default function Platforma() {
  const t = useT();
  const MODULES = [
    { icon: Calendar,   title: "PMS",                to: "/pms",                 text: t("Rezervimet, faturimi dhe operacionet e përditshme në një vend.", "Reservations, billing and daily operations in one place.") },
    { icon: Globe2,     title: "Booking Engine",     to: "/booking-engine",      text: t("Rezervime direkte në website-in tuaj — pa komisione kanalesh.",  "Direct bookings on your website — zero channel commissions.") },
    { icon: Repeat2,    title: "Channel Manager",    to: "/channel-manager",     text: t("Booking.com, Airbnb, Expedia, Agoda dhe më shumë — të lidhura me kalendarin tuaj.", "Booking.com, Airbnb, Expedia, Agoda and more — synced with your calendar.") },
    { icon: BedDouble,  title: t("Pastrimi","Housekeeping"), to: "/housekeeping", text: t("Statusi i dhomave dhe detyrat e pastrueseve, gjithmonë të përditësuara.", "Room status and housekeeping tasks, always up to date.") },
    { icon: Receipt,    title: "POS",                to: "/pos",                 text: t("Bar, restorant dhe room service — të lidhura me llogarinë e dhomës.", "Bar, restaurant and room service — tied to the room folio.") },
    { icon: TrendingUp, title: t("Çmimet dhe të Ardhurat","Pricing & Revenue"), to: "/revenue-management", text: t("Çmime që ndjekin sezonin, kërkesën dhe okupancën reale.", "Rates that follow season, demand and real occupancy.") },
    { icon: BarChart3,  title: t("Raporte","Reports"), to: "/platforma",         text: t("Okupanca, ADR, RevPAR dhe performanca e secilit kanal — të qarta.", "Occupancy, ADR, RevPAR and per-channel performance — crystal clear.") },
    { icon: Puzzle,     title: t("Integrime","Integrations"), to: "/integrimet", text: t("WhatsApp, email, SMS, pagesat, çelësat smart dhe sistemi fiskal.", "WhatsApp, email, SMS, payments, smart keys and the fiscal system.") },
    { icon: Sparkles,   title: "Check-in online",    to: "/pms",                 text: t("Klienti plotëson të dhënat para se të vijë — pa rradhë në recepsion.", "Guests fill in their details before arrival — no queue at the front desk.") },
  ];
  const BULLETS = [
    t("Në cloud — pa instalim në kompjuter",                "Cloud-based — no computer install"),
    t("Aksesueshme nga telefon, tablet ose laptop",         "Access from phone, tablet or laptop"),
    t("Backup-e automatike dhe siguri e provuar",            "Automated backups and proven security"),
    t("Mbështetje për disa objekte në një llogari",          "Multi-property support under one account"),
    t("API për integrime të dedikuara",                      "API for dedicated integrations"),
  ];
  const STATS = [
    ["99.95%",           t("Disponueshmëri","Uptime")],
    [t("Në çast","Instant"), t("Përditësim i kanaleve","Channel updates")],
    [t("48 orë","48 hours"), t("Konfigurim mesatar","Average setup")],
    ["24/7",             t("Mbështetje në shqip","Multilingual support")],
  ];
  return (
    <>
      <SEO
        title={t("Platforma Akomoda — Program i plotë hotelerie","Akomoda Platform — Complete hotel software")}
        description={t(
          "Akomoda bashkon rezervimet, recepsionin, booking engine, channel manager, pastrimin dhe raportet — të gjitha në një program të vetëm për hotele, resorte dhe apartamente.",
          "Akomoda unites reservations, front desk, booking engine, channel manager, housekeeping and reports — all in a single platform for hotels, resorts and apartments."
        )}
        canonical={typeof window !== 'undefined' ? window.location.href : ''}
      />
      <PageHeader
        eyebrow={t("Platforma","Platform")}
        title={t("Një program i vetëm për të menaxhuar hotelin","One platform to run the whole hotel")}
        description={t(
          "Rezervimet, recepsioni, pastrimi, POS-i, financat dhe raportet — të gjitha të lidhura me njëra-tjetrën dhe me Booking.com, Airbnb e Expedia.",
          "Reservations, front desk, housekeeping, POS, finance and reports — all connected to each other and to Booking.com, Airbnb and Expedia."
        )}
        ctas={[
          { to: "/demo",   label: t("Kërko Demo","Request Demo") },
          { to: "/cmimet", label: t("Shiko Çmimet","See Pricing"), variant: "ghost" },
        ]}
        breadcrumb={[{ label: t("Platforma","Platform") }]}
      />
      <section className="bg-surface-1">
        <div className="container-akm py-14 md:py-20">
          <div className="grid gap-px overflow-hidden rounded-xl border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
            {MODULES.map(({ icon: Icon, title, to, text }) => (
              <Link key={title} to={to} className="group flex flex-col gap-2 bg-white p-6 hover:bg-bg" data-testid={`module-${title.toLowerCase().replace(/\s+/g, '-')}`}>
                <Icon size={22} className="text-[#FF7A00]" />
                <div className="font-display text-base font-bold text-ink group-hover:text-[#FF7A00]">{title}</div>
                <p className="text-sm text-slate2">{text}</p>
                <span className="mt-2 text-sm font-semibold text-[#FF7A00]">{t("Mëso më shumë →","Learn more →")}</span>
              </Link>
            ))}
          </div>
        </div>
      </section>
      <section className="bg-surface-2">
        <div className="container-akm py-14 md:py-20">
          <div className="grid items-start gap-10 md:grid-cols-12">
            <div className="md:col-span-5">
              <div className="eyebrow">{t("Arkitektura","Architecture")}</div>
              <h2 className="h2-akm mt-3">{t("E ndërtuar për të rritur me ju","Built to grow with you")}</h2>
              <p className="lead mt-3">
                {t(
                  "Akomoda funksionon për një guest house me 5 dhoma, për një hotel me 80 dhoma, dhe për pronarët që menaxhojnë disa objekte njëkohësisht. I njëjti sistem. E njëjta thjeshtësi.",
                  "Akomoda works for a 5-room guest house, an 80-room hotel, and for owners running several properties at once. Same system. Same simplicity."
                )}
              </p>
              <ul className="mt-6 space-y-2 text-sm text-ink">
                {BULLETS.map((b) => (
                  <li key={b} className="flex items-start gap-2">
                    <span className="mt-2 h-1.5 w-1.5 shrink-0 rounded-full bg-[#FF7A00]" /> {b}
                  </li>
                ))}
              </ul>
            </div>
            <div className="md:col-span-7">
              <div className="grid grid-cols-2 gap-3">
                {STATS.map(([k, v]) => (
                  <div key={String(v)} className="card-akm">
                    <div className="font-display text-2xl font-extrabold text-ink">{k}</div>
                    <div className="text-sm text-slate2">{v}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>
      <FinalCTA />
    </>
  );
}
